import React, { useState, useEffect } from "react";
import { api, openSignedFile } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SecureImage } from "@/components/SecureImage";
import { StatusBadge } from "@/components/StatusBadge";
import { ProfileCodeChip } from "@/components/ProfileCodeChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Pencil, Save, X } from "lucide-react";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#F0ECE1] py-3 text-sm last:border-0">
      <span className="text-stone-500">{label}</span>
      <span className="text-right font-medium text-stone-800">{value || "—"}</span>
    </div>
  );
}

function AddressManager() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const loadA = async () => { try { const r = await api.get("/addresses"); setData(r.data); } catch (e) { toast.error(e.response?.data?.detail || "Could not load addresses"); } };
  useEffect(() => { loadA(); }, []);
  const sel = async (type) => { try { await api.post("/addresses/select", { type }); await loadA(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const saveNew = async () => { if (!val.trim()) { toast.error("Enter an address"); return; } setBusy(true); try { await api.post("/addresses", { address: val.trim() }); toast.success("Address added"); setAdding(false); setVal(""); await loadA(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const saveEdit = async () => { if (!val.trim()) { toast.error("Enter an address"); return; } setBusy(true); try { await api.put("/addresses", { address: val.trim() }); toast.success("Address updated"); setEditing(false); setVal(""); await loadA(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const del = async () => { setBusy(true); try { await api.delete("/addresses"); toast.success("Address removed"); await loadA(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  if (!data) return null;
  const Dot = ({ on }) => <span className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border ${on ? "border-[#0D5C3A] bg-[#0D5C3A]" : "border-stone-300"}`} />;
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm" data-testid="address-manager">
      <h3 className="mb-1 font-heading text-lg font-semibold">Addresses</h3>
      <p className="mb-4 text-sm text-stone-500">Your registered address is the default and can't be deleted. You may save one additional address (2 max). Only one can be selected at a time.</p>
      <div className="space-y-2">
        <button type="button" onClick={() => sel("default")} data-testid="profile-addr-default"
          className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm ${data.selected === "default" ? "border-[#0D5C3A] bg-emerald-50" : "border-[#E5E0D8]"}`}>
          <Dot on={data.selected === "default"} />
          <span className="flex-1"><b className="text-xs text-stone-400">Registered (default)</b><br />{data.default || "—"}</span>
        </button>
        {data.additional && !editing && (
          <div className={`rounded-lg border px-3 py-2 text-sm ${data.selected === "additional" ? "border-[#0D5C3A] bg-emerald-50" : "border-[#E5E0D8]"}`}>
            <button type="button" onClick={() => sel("additional")} className="flex w-full items-start gap-2 text-left" data-testid="profile-addr-additional">
              <Dot on={data.selected === "additional"} />
              <span className="flex-1"><b className="text-xs text-stone-400">Additional</b><br />{data.additional}</span>
            </button>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing(true); setVal(data.additional); }} data-testid="profile-addr-edit-btn"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={del} className="text-rose-600" data-testid="profile-addr-delete-btn"><X className="mr-1 h-3.5 w-3.5" /> Delete</Button>
            </div>
          </div>
        )}
        {editing && (
          <div className="space-y-2 rounded-lg border border-[#E5E0D8] p-3">
            <Textarea data-testid="profile-addr-edit-input" value={val} onChange={(e) => setVal(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={saveEdit} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="profile-addr-edit-save">Save</Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setVal(""); }}>Cancel</Button>
            </div>
          </div>
        )}
        {!data.additional && !adding && (
          <Button size="sm" variant="ghost" className="text-[#0D5C3A]" onClick={() => setAdding(true)} data-testid="profile-addr-add-btn"><Save className="mr-1 h-4 w-4" /> Add Additional Address</Button>
        )}
        {adding && (
          <div className="space-y-2 rounded-lg border border-[#E5E0D8] p-3">
            <Textarea data-testid="profile-addr-add-input" placeholder="Enter an additional address" value={val} onChange={(e) => setVal(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={saveNew} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="profile-addr-add-save">Save Address</Button>
              <Button size="sm" variant="outline" onClick={() => { setAdding(false); setVal(""); }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Profile() {
  const { business, user, refresh } = useAuth();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: business?.name, address: business?.address, owner_name: business?.owner_name, business_email: business?.business_email || "" });
  const [busy, setBusy] = useState(false);
  const isOwner = user?.role === "owner";

  const save = async () => {
    setBusy(true);
    try { await api.put("/profile", { ...form, business_email: form.business_email || null }); toast.success("Profile updated"); await refresh(); setEdit(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 pb-24 sm:pb-6" data-testid="profile-page">
      <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Business Profile</h1>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <SecureImage fileId={business?.logo_file_id} className="h-24 w-24 rounded-2xl object-cover" />
            <h2 className="mt-4 font-heading text-xl font-bold text-stone-900">{business?.name}</h2>
            <p className="text-sm capitalize text-stone-500">{BUSINESS_TYPE_LABELS[business?.business_type] || business?.business_type}</p>
            <div className="mt-3"><ProfileCodeChip code={business?.profile_code} /></div>
            <div className="mt-4 flex gap-2">
              <StatusBadge status={business?.registration_status} />
              <StatusBadge status={business?.subscription_status} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Business Information</h3>
            {isOwner && !edit && <Button variant="outline" size="sm" data-testid="profile-edit-btn" onClick={() => setEdit(true)}><Pencil className="mr-1 h-4 w-4" /> Edit</Button>}
          </div>
          {edit ? (
            <div className="space-y-4">
              <div><Label>Business Name</Label><Input data-testid="profile-name-input" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Owner Name</Label><Input data-testid="profile-owner-input" className="mt-1" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></div>
              <div><Label>Business Email (for reminders)</Label><Input data-testid="profile-email-input" type="email" className="mt-1" value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} /></div>
              <div><Label>Address</Label><Textarea data-testid="profile-address-input" className="mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="flex gap-2">
                <Button data-testid="profile-save-btn" disabled={busy} onClick={save} className="bg-[#0D5C3A] hover:bg-[#084229]"><Save className="mr-1 h-4 w-4" /> Save</Button>
                <Button variant="outline" onClick={() => setEdit(false)}><X className="mr-1 h-4 w-4" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <Row label="Owner Name" value={business?.owner_name} />
              <Row label="Business Email" value={business?.business_email} />
              <Row label="Verified Mobile" value={business?.owner_mobile} />
              <Row label="Business Address" value={business?.address} />
              <Row label="License Type" value={business?.license_type} />
              <Row label="License Number" value={business?.license_number} />
              <Row label="License Issue Date" value={business?.license_issue_date} />
              <Row label="License Expiry Date" value={business?.license_expiry_date} />
            </div>
          )}

          {business?.license_doc_file_id && (
            <button type="button" onClick={() => openSignedFile(business.license_doc_file_id).catch(() => toast.error("Could not open document"))}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#E5E0D8] px-4 py-2 text-sm font-medium text-[#0D5C3A] hover:bg-[#F0ECE1]" data-testid="profile-license-doc">
              <FileText className="h-4 w-4" /> View License Document
            </button>
          )}
        </div>
      </div>

      {isOwner && <AddressManager />}
    </div>
  );
}
